import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Checkbox,
  Row,
  Col,
} from "antd";

import {
  productGroupsActions,
  productBrandsActions,
  productsActions,
} from "../../../../store/slices/settings-slices";
import { productsService } from "../../../../services/settings-services";
import { getConfig } from "../../../../config";

const ProductsCreateProductModal = ({ setIsOpen }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { productGroups, trigger: groupTrigger } = useSelector(
    (store) => store.productGroups
  );
  const { productBrands, trigger: brandTrigger } = useSelector(
    (store) => store.productBrands
  );
  const { t } = useTranslation();
  const { cityCode } = getConfig();

  useEffect(() => {
    dispatch(productGroupsActions.getAllProductGroups());
    dispatch(productBrandsActions.getAllProductBrands());
  }, [groupTrigger, brandTrigger, dispatch]);

  const submitHandler = async (values) => {
    try {
      const formattedData = {
        name: {
          en: values.nameEn,
          ru: values.nameRu,
          ge: values.nameKa || values.nameAr,
          ar: values.nameAr || values.nameKa,
        },
        description: {
          en: values.descriptionEn || "",
          ru: values.descriptionRu || "",
          ge: values.descriptionKa || values.descriptionAr || "",
          ar: values.descriptionAr || values.descriptionKa || "",
        },
        energy: Number(values.energy) || 0,
        proteins: Number(values.proteins) || 0,
        fats: Number(values.fats) || 0,
        carbohydrates: Number(values.carbohydrates) || 0,
        price: parseFloat(values.price) || 0,
        groupId: values.groupId || null,
        brandId: values.brandId || null,
        excludable: values.excludable || false,
      };

      await productsService.createProduct(formattedData);
      dispatch(productsActions.changeTrigger());
      toast.success("Product created successfully!");
      form.resetFields();
      setIsOpen(false);
    } catch (error) {
      console.error(t("Error creating product"), error);
      toast.error(t("Error creating product"));
    }
  };

  return (
    <Modal
      title={t("modal-products-new-product")}
      open={true}
      onCancel={() => setIsOpen(false)}
      onOk={() => form.submit()}
      okText="Save"
      cancelText="Cancel"
      width={900}
      centered
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={submitHandler}
        initialValues={{
          energy: 0,
          proteins: 0,
          fats: 0,
          carbohydrates: 0,
          price: 0,
          excludable: false,
        }}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label={`${t("modal-name-input-new-category")} (English)`}
              name="nameEn"
              rules={[{ required: true, message: "This field is required" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label={`${t("modal-name-input-new-category")} (Русский)`}
              name="nameRu"
              rules={[{ required: true, message: "This field is required" }]}
            >
              <Input />
            </Form.Item>

            {cityCode === "UAE" ? (
              <Form.Item
                label={`${t("modal-name-input-new-category")} (العربية)`}
                name="nameAr"
                rules={[{ required: true, message: "This field is required" }]}
              >
                <Input />
              </Form.Item>
            ) : (
              <Form.Item
                label={`${t("modal-name-input-new-category")} (ქართული)`}
                name="nameKa"
                rules={[{ required: true, message: "This field is required" }]}
              >
                <Input />
              </Form.Item>
            )}

            <Form.Item
              label={`${t("modal-Description-input")} (English)`}
              name="descriptionEn"
            >
              <Input />
            </Form.Item>

            <Form.Item
              label={`${t("modal-Description-input")} (Русский)`}
              name="descriptionRu"
            >
              <Input />
            </Form.Item>

            {cityCode === "UAE" ? (
              <Form.Item
                label={`${t("modal-Description-input")} (العربية)`}
                name="descriptionAr"
              >
                <Input />
              </Form.Item>
            ) : (
              <Form.Item
                label={`${t("modal-Description-input")} (ქართული)`}
                name="descriptionKa"
              >
                <Input />
              </Form.Item>
            )}

            <Form.Item label="Group" name="groupId">
              <Select
                placeholder="Select a group"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {productGroups &&
                  productGroups.map((group) => (
                    <Select.Option key={group.id} value={group.id}>
                      {group.name.en}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>

            <Form.Item label="Brand" name="brandId">
              <Select
                placeholder="Select a brand"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {productBrands &&
                  productBrands.map((brand) => (
                    <Select.Option key={brand.id} value={brand.id}>
                      {brand.name?.en || brand.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={`${t("modal-energy")} (kcal)`} name="energy">
              <InputNumber style={{ width: "100%" }} min={0} />
            </Form.Item>

            <Form.Item label={`${t("modal-proteins")} (g)`} name="proteins">
              <InputNumber style={{ width: "100%" }} min={0} step={0.1} />
            </Form.Item>

            <Form.Item label={`${t("modal-fats")} (g)`} name="fats">
              <InputNumber style={{ width: "100%" }} min={0} step={0.1} />
            </Form.Item>

            <Form.Item
              label={`${t("modal-carbohydrates")} (g)`}
              name="carbohydrates"
            >
              <InputNumber style={{ width: "100%" }} min={0} step={0.1} />
            </Form.Item>

            <Form.Item label={`${t("modal-price")} (₾/kg)`} name="price">
              <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
            </Form.Item>

            <Form.Item name="excludable" valuePropName="checked">
              <Checkbox>{t("modal-excludable")}</Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export { ProductsCreateProductModal };
